import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, FormGroup, Label, Input } from 'reactstrap';

interface PropertyRejectionReasonModalProps {
  isOpen: boolean;
  toggle: () => void;
  onConfirm: (reason: string) => void;
  isLoading?: boolean;
}

const reasonOptions = [
  'Property documentation is incomplete or missing.',
  'Property does not meet safety and regulatory standards.',
  'Property address or location information is incorrect.',
  'Property images are unclear or insufficient.',
  'Property pricing or financial information is inaccurate.',
  'Property description does not match actual conditions.',
  'Property lacks necessary permits or certifications.',
  'Property inspection report shows major defects.',
  'Others',
];

const PropertyRejectionReasonModal: React.FC<PropertyRejectionReasonModalProps> = ({ 
  isOpen, 
  toggle, 
  onConfirm,
  isLoading = false
}) => {
  const [selectedReason, setSelectedReason] = useState('');
  const [customReason, setCustomReason] = useState('');

  const maxCustomReasonLength = 250;

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedReason('');
      setCustomReason('');
    }
  }, [isOpen]);

  const handleConfirm = () => {
    let finalReason = '';
    
    if (selectedReason === 'Others') {
      finalReason = customReason.trim();
    } else {
      finalReason = selectedReason;
    }

    if (finalReason) {
      onConfirm(finalReason);
      // Don't call toggle() here - let parent handle modal closing after API call
    }
  };

  const isFormValid = () => {
    if (!selectedReason) return false;
    if (selectedReason === 'Others') {
      return customReason.trim().length > 0 && customReason.trim().length <= maxCustomReasonLength;
    }
    return true;
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="md">
      <ModalHeader toggle={toggle} className="fw-bold" style={{ color: 'black' }}>
        Property Rejection Reason
      </ModalHeader>
      <ModalBody className="pt-0 pb-2 px-3 px-md-4">
        <hr className="mt-3"/>
        
        <FormGroup className="mb-4">
          <Label className="fw-bold mb-2">Select Rejection Reason</Label>
          <Input
            type="select"
            value={selectedReason}
            onChange={e => setSelectedReason(e.target.value)}
            style={{ height: 48, fontSize: 16 }}
            disabled={isLoading}
          >
            <option value="" disabled hidden>-- Select a reason --</option>
            {reasonOptions.map((reason, idx) => (
              <option key={idx} value={reason}>{reason}</option>
            ))}
          </Input>
        </FormGroup>

        {/* Show custom reason textarea only when "Others" is selected */}
        {selectedReason === 'Others' && (
          <FormGroup className="mb-3">
            <Label className="fw-bold mb-2">
              Write Custom Reason
              <span className="text-muted ms-2">({customReason.length}/{maxCustomReasonLength})</span>
            </Label>
            <Input
              type="textarea"
              rows={5}
              placeholder="Write your custom reason here..."
              value={customReason}
              onChange={e => {
                if (e.target.value.length <= maxCustomReasonLength) {
                  setCustomReason(e.target.value);
                }
              }}
              style={{ fontSize: 16, resize: 'none' }}
              className={customReason.length > maxCustomReasonLength ? 'is-invalid' : ''}
              disabled={isLoading}
            />
            {customReason.length > maxCustomReasonLength && (
              <div className="invalid-feedback">
                Maximum {maxCustomReasonLength} characters allowed
              </div>
            )}
          </FormGroup>
        )}
      </ModalBody>
      
      <ModalFooter className="justify-content-end gap-2">
        <Button 
          color="danger" 
          onClick={toggle}
          style={{ minWidth: '100px' }}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button 
          color="success" 
          onClick={handleConfirm}
          disabled={!isFormValid() || isLoading}
          style={{ minWidth: '100px' }}
        >
          {isLoading ? 'Rejecting...' : 'Confirm Rejection'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default PropertyRejectionReasonModal;